import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import {
  Template,
  CopyTreatmentsRequest,
  CopyTreatmentsResponse,
  SalesOrderPeriod,
  CopyTemplatesByYearRequest,
  CopyTemplatesByYearResponse,
  GetTemplatesResponse,
  TemplateFilter,
  Treatment,
  TreatmentSchema,
  TemplateType,
  UpdateDefaultTreatmentSchemaIdForTemplateRequest,
  NutrientDesignation,
  NutrientSource,
  NutrientProperties,
  UpsertNutrientPropertiesRequest,
  SoilType,
  UnitOfMeasure,
  VarietyProperties,
  UpsertVarietyPropertiesRequest,
  CropVariety,
  ProductionPurposeCategory,
  NitrogenModelProperties,
  UpsertNitrogenModelPropertiesRequest,
  ProductionPurposeCategoryDetailed,
  UpsertProductionPurposeCategoryRequest,
} from '../models';

interface TemplateMutationResponse {
  id: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.API_BASE_URL + '/service/field-advice-admin/secure/';
  protected templateBaseUrl = this.env.APIM_BASE_URL + '/arable-service/fieldadvice/template';
  protected periodBaseUrl =
    this.env.APIM_BASE_URL +
    (this.env.PRE_SALES_REBUILD_DONE ? '/arable-service/' : '/arable-admin-service/');
  protected automaticAdviceBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/automatic-advice/';
  protected nutrientBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service/nutrients/';
  protected nutrientPropertiesBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/nutrient-properties/';
  protected soilTypeBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service/soil-types/';
  protected unitOfMeasureBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/units-of-measure/';
  protected automaticCropFieldExportBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/automatic-export/';
  protected automaticCropFieldOrderBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/automatic-order/';
  protected varietyPropertiesBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/variety-properties/';
  protected varietyPropertiesBemBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/variety-properties-bem/';
  protected cropVarietyBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service/crop-variety/';
  protected productionPurposeCategoryBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/production-purpose-category/';
  protected nitrogenModelPropertiesBaseUrl =
    this.env.APIM_BASE_URL + '/arable-advice-service/nitrogen-model-properties/';

  getTemplates(
    cropYear: number,
    pageNumber?: number,
    pageSize?: number,
    filter?: TemplateFilter,
  ): Observable<GetTemplatesResponse> {
    const paginationParams: { [key: string]: string } = {};
    if (pageNumber && pageNumber > 0) {
      paginationParams.pageNumber = pageNumber.toString();
    }
    if (pageSize && pageSize > 0) {
      paginationParams.pageSize = pageSize.toString();
    }

    let params = new HttpParams({ fromObject: paginationParams });
    if (filter) {
      if (filter.cropId && filter.cropId !== null) {
        params = params.append('cropId', filter.cropId.toString());
      }
      if (filter.soilTypeId && filter.soilTypeId !== null) {
        params = params.append('soilTypeId', filter.soilTypeId.toString());
      } else if (filter.soilTypeId === 0) {
        params = params.append('withoutSoilType', 'true');
      }
      if ((filter.isCorrupt || filter.isCorrupt === false) && filter.isCorrupt !== null) {
        params = params.append('isCorrupt', filter.isCorrupt.toString());
      }
      if (filter.templateType && filter.templateType !== null) {
        params = params.append('templateType', filter.templateType);
      }
      if (filter.provinces) {
        params = params.append('province', filter.provinces.toString());
      }
      if (filter.cropAidId) {
        params = params.append('cropAidId', filter.cropAidId.toString());
      }
    }

    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.get<GetTemplatesResponse>(`${this.baseUrl}template/cropyear/${cropYear}`, {
        params,
      });
    }

    return this.http.get<GetTemplatesResponse>(`${this.templateBaseUrl}/cropyear/${cropYear}`, {
      params,
    });
  }

  getTemplate(id: number): Observable<Template> {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.get<Template>(`${this.baseUrl}template/${id}`);
    }

    return this.http.get<Template>(`${this.templateBaseUrl}/${id}`);
  }

  upsertTreatment(treatment: Treatment, template: Template): Observable<TemplateMutationResponse> {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post<{ id: number }>(
        `${this.baseUrl}template/${template.id}/treatments`,
        treatment,
      );
    }

    return this.http.post<{ id: string }>(
      `${this.templateBaseUrl}/${template.id}/treatments`,
      treatment,
    );
  }

  removeTreatment(treatment: Treatment, template: Template) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.delete(`${this.baseUrl}template/${template.id}/treatments/${treatment.id}`);
    }

    return this.http.delete(`${this.templateBaseUrl}/${template.id}/treatments/${treatment.id}`);
  }

  upsertTreatmentSchema(
    treatmentSchema: TreatmentSchema,
    template: Template,
  ): Observable<TemplateMutationResponse> {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post<{ id: number }>(
        `${this.baseUrl}template/${template.id}/schemas`,
        treatmentSchema,
      );
    }

    return this.http.post<{ id: string }>(
      `${this.templateBaseUrl}/${template.id}/schemas`,
      treatmentSchema,
    );
  }

  removeTreatmentSchema(treatmentSchema: TreatmentSchema, template: Template) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.delete(
        `${this.baseUrl}template/${template.id}/schemas/${treatmentSchema.id}`,
      );
    }

    return this.http.delete(`${this.templateBaseUrl}/${template.id}/schemas/${treatmentSchema.id}`);
  }

  upsertTemplate(template: Template): Observable<{ id: number }> {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post<{ id: number }>(`${this.baseUrl}template`, template);
    }

    return this.http.post<{ id: number }>(`${this.templateBaseUrl}`, template);
  }

  removeTemplate(id: number) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.delete(`${this.baseUrl}template/${id}`);
    }

    return this.http.delete(`${this.templateBaseUrl}/${id}`);
  }

  copyTreatments(request: CopyTreatmentsRequest) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post<CopyTreatmentsResponse>(
        `${this.baseUrl}template/copy-treatments`,
        request,
      );
    }

    return this.http.post<CopyTreatmentsResponse>(
      `${this.templateBaseUrl}/copy-treatments`,
      request,
    );
  }

  getSalesOrderPeriods(cropYear: number) {
    if (this.env.PRE_SALES_REBUILD_DONE) {
      return this.http
        .get<{
          salesOrderPeriods: SalesOrderPeriod[];
        }>(`${this.periodBaseUrl}salesorderperiod?year=${cropYear}`)
        .pipe(
          map((response) =>
            response.salesOrderPeriods.filter(
              (p) =>
                p.periodType === TemplateType.GBM || p.periodType === TemplateType.Fertilization,
            ),
          ),
        );
    }

    return this.http
      .get<SalesOrderPeriod[]>(`${this.periodBaseUrl}salesorderperiod?year=${cropYear}`)
      .pipe(
        map((salesOrderPeriods) =>
          salesOrderPeriods.filter(
            (p) => p.periodType === TemplateType.GBM || p.periodType === TemplateType.Fertilization,
          ),
        ),
      );
  }

  copyTemplatesByYear(request: CopyTemplatesByYearRequest) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}template/copy-templates-by-year`, request);
    }

    return this.http.post<CopyTemplatesByYearResponse>(
      `${this.templateBaseUrl}/copy-templates-by-year`,
      request,
    );
  }

  updateDefaultTreatmentSchemaIdForTemplate(
    templateId: number,
    request: UpdateDefaultTreatmentSchemaIdForTemplateRequest,
  ) {
    if (!this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE) {
      return this.http.post(
        `${this.baseUrl}template/${templateId}/defaultTreatmentSchema`,
        request,
      );
    }

    return this.http.post(`${this.templateBaseUrl}/${templateId}/defaultTreatmentSchema`, request);
  }

  getNutrientDesignations(): Observable<NutrientDesignation[]> {
    return this.http.get<NutrientDesignation[]>(`${this.nutrientBaseUrl}designations`);
  }

  getAllNutrientSources(): Observable<NutrientSource[]> {
    return this.http.get<NutrientSource[]>(`${this.nutrientBaseUrl}sources`);
  }

  getAllNutrientProperties(): Observable<NutrientProperties[]> {
    return this.http.get<NutrientProperties[]>(`${this.nutrientPropertiesBaseUrl}`);
  }

  upsertNutrientProperties(request: UpsertNutrientPropertiesRequest): Observable<void> {
    return this.http.post<void>(`${this.nutrientPropertiesBaseUrl}`, request);
  }

  deleteNutrientProperties(id: number): Observable<void> {
    return this.http.delete<void>(`${this.nutrientPropertiesBaseUrl}${id}`);
  }

  getAllSoilTypes(): Observable<SoilType[]> {
    return this.http.get<SoilType[]>(`${this.soilTypeBaseUrl}`);
  }

  getAllUnitsOfMeasure(): Observable<UnitOfMeasure[]> {
    return this.http.get<UnitOfMeasure[]>(`${this.unitOfMeasureBaseUrl}`);
  }

  getAllVarietyProperties(): Observable<VarietyProperties[]> {
    return this.http.get<VarietyProperties[]>(`${this.varietyPropertiesBemBaseUrl}`);
  }

  upsertVarietyProperties(request: UpsertVarietyPropertiesRequest): Observable<void> {
    return this.http.post<void>(`${this.varietyPropertiesBemBaseUrl}`, request);
  }

  deleteVarietyProperties(id: number): Observable<void> {
    return this.http.delete<void>(`${this.varietyPropertiesBemBaseUrl}${id}`);
  }

  getAllCropVarieties(): Observable<CropVariety[]> {
    return this.http.get<CropVariety[]>(`${this.cropVarietyBaseUrl}`);
  }

  getAllProductionPurposeCategories(): Observable<ProductionPurposeCategory[]> {
    return this.http.get<ProductionPurposeCategory[]>(`${this.productionPurposeCategoryBaseUrl}`);
  }

  getAllNitrogenModelProperties(): Observable<NitrogenModelProperties[]> {
    return this.http.get<NitrogenModelProperties[]>(`${this.nitrogenModelPropertiesBaseUrl}`);
  }

  upsertNitrogenModelProperties(request: UpsertNitrogenModelPropertiesRequest): Observable<void> {
    return this.http.post<void>(`${this.nitrogenModelPropertiesBaseUrl}`, request);
  }

  deleteNitrogenModelProperties(id: number): Observable<void> {
    return this.http.delete<void>(`${this.nitrogenModelPropertiesBaseUrl}${id}`);
  }

  getProductionPurposeCategoriesDetailed(): Observable<ProductionPurposeCategoryDetailed[]> {
    return this.http.get<ProductionPurposeCategoryDetailed[]>(
      `${this.productionPurposeCategoryBaseUrl}detailed`,
    );
  }

  deleteProductionPurposeCategories(id: number): Observable<void> {
    return this.http.delete<void>(`${this.productionPurposeCategoryBaseUrl}${id}`);
  }

  upsertProductionPurposeCategory(
    request: UpsertProductionPurposeCategoryRequest,
  ): Observable<void> {
    return this.http.post<void>(`${this.productionPurposeCategoryBaseUrl}`, request);
  }
}
